import { BodyText, Button, Tooltip, toast } from "@/components/ui";
import { useRemoveDiagnosisFromVisit } from "@/features/Charting/network/useRemoveDiagnosisFromVisit";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, LoaderCircle, Trash2 } from "lucide-react";

interface DiagnosisProps {
  conditionId?: string;
  isReordering?: boolean;
  label: string;
  motherEncounterId: string;
  pending?: boolean;
  patientId: string;
  sortableId?: string;
}

export const Diagnosis = ({
  conditionId,
  isReordering = false,
  label,
  motherEncounterId,
  pending = false,
  patientId,
  sortableId,
}: DiagnosisProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId ?? conditionId ?? label,
    disabled: pending || isReordering || !conditionId,
  });
  const removeDiagnosisFromVisit = useRemoveDiagnosisFromVisit({
    patientId,
    motherEncounterId,
    onError: () => toast.error("Failed to remove diagnosis from visit"),
  });
  const removeLabel = `Remove ${label} from visit`;

  return (
    <div
      aria-busy={pending || undefined}
      aria-label={pending ? `Adding ${label} to visit` : undefined}
      className={`flex items-center gap-2 rounded-lg border border-primary bg-white px-2 py-1.5 ${isDragging ? "z-10 opacity-50" : ""}`}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Button
        aria-label={`Drag ${label} to reorder`}
        className="relative flex h-4 w-4 shrink-0 cursor-grab touch-none items-center justify-center text-disabled before:absolute before:-inset-2 active:cursor-grabbing"
        size="snatched"
        title="Drag to reorder"
        variant="icon"
        disabled={pending || isReordering || !conditionId}
        {...attributes}
        {...listeners}
      >
        <GripVertical aria-hidden size={16} />
      </Button>
      <BodyText
        bold
        className="min-w-0 flex-1 break-words"
        size="sm"
        textColor="link"
      >
        {label}
      </BodyText>
      {pending ? (
        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
          <LoaderCircle
            aria-hidden="true"
            className="h-4 w-4 animate-spin text-link"
          />
        </div>
      ) : (
        <Tooltip
          tooltipContentText="Remove from Visit"
          triggerClassName="h-4 w-4"
        >
          <Button
            aria-label={removeLabel}
            className="h-4 w-4"
            isLoading={removeDiagnosisFromVisit.isPending}
            disabled={removeDiagnosisFromVisit.isPending || !conditionId}
            onClick={() => {
              if (conditionId) {
                removeDiagnosisFromVisit.mutate({ conditionId });
              }
            }}
            size="icon"
            variant="icon"
          >
            <Trash2 aria-hidden="true" className="h-4 w-4" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
